import { Card } from "../../../components/Card";
import { QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast } from "../../../utils";
import { Page } from "../../../components/Page";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  CSRDKeys,
  useFinaliseCSRDQuestionnaire,
  useGetCSRDQuestionnaireDataById,
  useGetCSRDQuestionnaireDataComments,
  useGetCSRDQuestionsByType,
  useUpdateCSRDQuestionnaireDataById
} from "../../../hooks/query/csrd";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { Flex, Heading, IconButton, Text, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { AssigneeHeader } from "../../../components/AssigneeHeader";
import { NavigationTabs } from "../../../components/NavigationTabs";
import { CSRD_QUESTION_COMPLETED, mapCSRDTopicsData } from "../helpers";
import { useQueryClient } from "react-query";
import { FaFile } from "react-icons/fa";
import { AttachedFilesModal } from "./index";

function CSRDQuestionnaire({ questionnaireId, isOpenedInModal }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "csrd.questionnaire"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    data: questionnaireData
  } = useGetCSRDQuestionnaireDataById(questionnaireId ?? null, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: questionnaireId !== null
  });

  const {
    data: questionsData
  } = useGetCSRDQuestionsByType({ type: questionnaireData?.type ?? "" }, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: questionnaireData !== null && typeof questionnaireData !== "undefined"
  });

  const {
    data: comments
  } = useGetCSRDQuestionnaireDataComments({
    type: questionnaireData?.type ?? "",
    year: questionnaireData?.year ?? ""
  }, {
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: questionnaireData !== null && typeof questionnaireData !== "undefined"
  });

  const handleErrors = (err) => {
    const handleGenericError = (error) => {
      showErrorToast(toast, error.response?.data?.parameters?.message ?? error.message);
    }

    if (err.response?.status === 400) {
      try {
        const obj = JSON.parse(err.response?.data?.parameters?.message);
        if (obj && obj.length > 0) {
          const errorEl = obj[0];
          let questionDetails;

          const foundQuestion = questionsData?.find(q => q.id === errorEl.questionId) ?? undefined;
          if (typeof foundQuestion !== "undefined") {
            questionDetails = `${t("topic")}: "${foundQuestion.topic}", ${t("question")}: "${foundQuestion.question}"`;
          } else {
            questionDetails = `${t("questionWithId")}: ${errorEl.questionId}`;
          }

          const parsedErrorsText = Object.keys(errorEl)
            .filter(key => key !== "questionId")
            .map(key => {
              return `${key}: ${errorEl[key]}`;
            })
            .join(", ");

          const errorText = `${questionDetails} - ${parsedErrorsText}`;
          showErrorToast(toast, errorText);
        } else {
          handleGenericError(err);
        }
      } catch (parseError) {
        handleGenericError(err);
      }
    } else {
      handleGenericError(err);
    }
  };

  const { mutate: updateQuestionnaireData } = useUpdateCSRDQuestionnaireDataById();
  const { mutate: finalise } = useFinaliseCSRDQuestionnaire({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, t("finalisedSuccessfully"));
      queryClient.invalidateQueries(CSRDKeys.CSRDQuestionnaireById());
    },
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const saveMethod = (csrdData, isFinalise) => {
    if (csrdData) {
      const apiData = {
        ...questionnaireData,
        questions: csrdData.map(el => {
          const objToReturn = {
            ...el
          };

          if (el.status === true) {
            objToReturn.status = CSRD_QUESTION_COMPLETED;
          } else {
            delete objToReturn.status;
          }

          return objToReturn;
        })
      };

      updateQuestionnaireData({
        id: questionnaireId,
        data: apiData
      }, {
        onMutate: () => dispatch(showAppSpinner()),
        onSuccess: () => {
          if (isFinalise) {
            finalise(questionnaireId);
          } else {
            showSuccessToast(toast, t("savedSuccessfully"));
            queryClient.invalidateQueries(CSRDKeys.CSRDQuestionnaireById());
            queryClient.invalidateQueries(CSRDKeys.allCSRDAnswersByQuestionId());
          }
        },
        onError: err => handleErrors(err),
        onSettled: () => dispatch(hideAppSpinner())
      });
    } else {
      if (isFinalise) {
        finalise(questionnaireId);
      }
    }
  };

  const prepareData = (questionsDefinitions, questionnaireData, comments) => {
    return {
      comments: comments,
      questionnaireData: questionnaireData,
      questionsDefinitions: questionsDefinitions,
      uniqueTopics: questionsDefinitions
        .map(q => q.topic)
        .filter((value, index, self) => self.indexOf(value) === index)
    };
  };

  const customHeaderMargin = useBreakpointValue({
    base: 0,
    lg: 8
  });

  const {
    isOpen: isAttachedFilesModalOpen,
    onOpen: onAttachedFilesModalOpen,
    onClose: onAttachedFilesModalClose
  } = useDisclosure();

  const renderContent = () => {
    return questionnaireData ? (
      <>
        <AssigneeHeader
          assignee={{ name: questionnaireData.assignee }}
          year={questionnaireData.year}
          customComponent={
            <Flex alignItems="center" marginInlineStart={customHeaderMargin} gap={8}>
              <Flex gap={2} alignItems="center">
                <Text fontSize="sm" opacity="0.75">
                  {t("questionnaireType")}:
                </Text>
                <Heading size="xs">
                  {questionnaireData.type}
                </Heading>
              </Flex>
              <Flex gap={2} alignItems="center">
                <Text fontSize="sm" opacity="0.75" as="span">
                  {t("attachedFiles")}:
                </Text>
                <IconButton
                  aria-label={"open-attached-files-modal-btn"}
                  type="button"
                  icon={<FaFile size="16" />}
                  minWidth="20px"
                  padding={"5px"}
                  variant="ghost"
                  color={"esg_green"}
                  onClick={() => onAttachedFilesModalOpen()}
                  title={t("attachButtonTitle")}
                />
              </Flex>
            </Flex>
          }
        />
        {questionsData && comments &&
          <NavigationTabs
            elements={mapCSRDTopicsData(prepareData(questionsData, questionnaireData, comments))}
            tabTitle={null}
            subTitleAttribute={null}
            saveMethod={isOpenedInModal || questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED ?
              null : saveMethod}
            formSubmission={{
              allowed: true,
              requested: false,
              decline: () => {
              }
            }}
            tabsContainerStyles={{ height: "calc(100% - 60px)" }}
            tabsStyles={{ maxBlockSize: "none", height: "calc(100% - 70px)" }}
            buttonsPanelStyles={{ marginBlockStart: 0, paddingBottom: 0 }}
            navTabPanelStyles={{paddingInlineEnd: 0, paddingInlineStart: 1}}
          />
        }
        <AttachedFilesModal
          isOpen={isAttachedFilesModalOpen}
          onClose={onAttachedFilesModalClose}
          isOpenedInModal={isOpenedInModal}
          questionnaireData={questionnaireData}
        />
      </>
    ) : <Text>{globalTranslation("noDataAvailable")}</Text>;
  };

  return (
    !isOpenedInModal ?
      <Page>
        <Card marginBlock={0} height={"100%"}>
          {renderContent()}
        </Card>
      </Page> : renderContent()
  );
}

export default CSRDQuestionnaire;